import { i18n } from "@/shared/i18n"
import { isObject } from "@/shared/lib"

import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"

interface TriggerWordDuplicate {
  word: string
  existing_list_name: string
  existing_list_index: number
}

const isNonEmptyArray = (value: unknown): value is [unknown, ...unknown[]] => Array.isArray(value) && value.length > 0

const isTriggerWordDuplicate = (value: unknown): value is TriggerWordDuplicate => {
  return (
    isObject(value) &&
    typeof value.word === "string" &&
    typeof value.existing_list_name === "string" &&
    typeof value.existing_list_index === "number"
  )
}

const hasDuplicatesArray = (data: unknown): data is { error: Array<{ duplicates: TriggerWordDuplicate[] }> } => {
  if (!isObject(data) || !("error" in data) || !isNonEmptyArray(data.error)) return false
  const firstError = data.error[0]
  return (
    isObject(firstError) &&
    "duplicates" in firstError &&
    isNonEmptyArray(firstError.duplicates) &&
    firstError.duplicates.every(isTriggerWordDuplicate)
  )
}

export const toDuplicateWordsCustomError = (
  error: FetchBaseQueryError
): { status: "CUSTOM_ERROR"; error: string } | null => {
  if (error.status !== 409) return null
  if (!hasDuplicatesArray(error.data)) return null

  const details = error.data.error[0].duplicates
    .map(duplicate => `"${duplicate.word}" (${duplicate.existing_list_name})`)
    .join(", ")

  return {
    status: "CUSTOM_ERROR",
    error: i18n.t("word_triggers-duplicate-words", "Duplicate words found: {{ details }}", { details })
  }
}
